import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { TpvOperatorRepository } from '@app/domain/ports/tpv/tpv-operator.repository';
import {
  TpvOperatorResult,
  TpvOperatorListItem,
  TpvOperatorDetail,
  OperatorMerchants,
} from '@app/domain/models/tpv/tpv-operator.model';
import {
  CreateTpvOperatorRequest,
  UpdateTpvOperatorRequest,
} from '@app/data/dtos/tpv/tpv-operator.dto';

/**
 * Mock de operadores TPV para desarrollo sin backend (`tpvUseMocks`).
 * Mantiene un roster en memoria para que el listado (§4.B) y el editor de
 * allow-list (§4.C) tengan comportamiento realista entre llamadas.
 */
@Injectable()
export class TpvOperatorMockDataSource extends TpvOperatorRepository {
  private readonly roster: TpvOperatorListItem[] = [
    {
      idUser: 312,
      email: 'juan.operador@ejemplo.com',
      name: 'Juan Pérez',
      isActive: true,
      idGestor: 140,
      role: { id: 3, name: 'Solo ver' },
      companyWide: false,
      merchants: ['019e8a80-d01a-7183-91a1-6c5c1f3d5f05'],
    },
    {
      idUser: 318,
      email: 'ana.admin@ejemplo.com',
      name: 'Ana Admin',
      isActive: true,
      idGestor: 142,
      role: { id: 2, name: 'Administrador' },
      companyWide: true,
      merchants: [],
    },
  ];

  override createOperator(
    idCompany: number | string,
    request: CreateTpvOperatorRequest,
  ): Observable<TpvOperatorResult> {
    const merchants = request.id_merchants ?? [];
    const idUser = 320 + this.roster.length;
    this.roster.push({
      idUser,
      email: request.email,
      name: `${request.first_name} ${request.last_name}`.trim(),
      isActive: true,
      idGestor: 140 + this.roster.length,
      role: { id: request.rol_id ?? 3, name: request.rol_id === 2 ? 'Administrador' : 'Solo ver' },
      companyWide: merchants.length === 0,
      merchants,
    });
    return of<TpvOperatorResult>({
      idUser,
      email: request.email,
      polipayAccount: '0000000312',
      idGestor: 140,
      role: { id: request.rol_id ?? 3, name: 'Solo ver' },
      idCompany: Number(idCompany),
      merchants,
    }).pipe(delay(500));
  }

  override listOperators(_idCompany: number | string): Observable<TpvOperatorListItem[]> {
    return of(this.roster.map((o) => ({ ...o, merchants: [...o.merchants] }))).pipe(delay(400));
  }

  override getOperator(
    _idCompany: number | string,
    idUser: number | string,
  ): Observable<TpvOperatorDetail> {
    const op = this.find(idUser);
    const parts = (op?.name ?? '').trim().split(/\s+/);
    return of<TpvOperatorDetail>({
      idUser: Number(idUser),
      email: op?.email ?? '',
      name: op?.name ?? '',
      isActive: op?.isActive ?? true,
      idGestor: op?.idGestor ?? 0,
      role: op?.role ?? { id: 3, name: 'Solo ver' },
      companyWide: op?.companyWide ?? true,
      merchants: [...(op?.merchants ?? [])],
      firstName: parts[0] ?? '',
      lastName: parts.slice(1).join(' '),
      phoneNumber: '',
    }).pipe(delay(300));
  }

  override updateOperator(
    idCompany: number | string,
    idUser: number | string,
    request: UpdateTpvOperatorRequest,
  ): Observable<TpvOperatorDetail> {
    const op = this.find(idUser);
    if (op) {
      const first = request.first_name ?? op.name.split(/\s+/)[0] ?? '';
      const last = request.last_name ?? op.name.split(/\s+/).slice(1).join(' ');
      op.name = `${first} ${last}`.trim();
      if (request.rol_id != null) {
        op.role = { id: request.rol_id, name: request.rol_id === 2 ? 'Administrador' : 'Solo ver' };
      }
    }
    return this.getOperator(idCompany, idUser);
  }

  override deleteOperator(
    _idCompany: number | string,
    idUser: number | string,
  ): Observable<void> {
    const op = this.find(idUser);
    if (op) op.isActive = false;
    return of(void 0).pipe(delay(300));
  }

  override getOperatorMerchants(
    _idCompany: number | string,
    idUser: number | string,
  ): Observable<OperatorMerchants> {
    return of(this.snapshot(idUser)).pipe(delay(300));
  }

  override replaceOperatorMerchants(
    _idCompany: number | string,
    idUser: number | string,
    idMerchants: string[],
  ): Observable<OperatorMerchants> {
    const op = this.find(idUser);
    if (op) op.merchants = [...new Set(idMerchants)];
    return of(this.snapshot(idUser)).pipe(delay(400));
  }

  override addOperatorMerchants(
    _idCompany: number | string,
    idUser: number | string,
    idMerchants: string[],
  ): Observable<OperatorMerchants> {
    const op = this.find(idUser);
    if (op) op.merchants = [...new Set([...op.merchants, ...idMerchants])];
    return of(this.snapshot(idUser)).pipe(delay(400));
  }

  override removeOperatorMerchant(
    _idCompany: number | string,
    idUser: number | string,
    idMerchant: string,
  ): Observable<OperatorMerchants> {
    const op = this.find(idUser);
    if (op) op.merchants = op.merchants.filter((m) => m !== idMerchant);
    return of(this.snapshot(idUser)).pipe(delay(400));
  }

  private find(idUser: number | string): TpvOperatorListItem | undefined {
    return this.roster.find((o) => o.idUser === Number(idUser));
  }

  /** Snapshot inmutable con company_wide derivado (vacío = company-wide). */
  private snapshot(idUser: number | string): OperatorMerchants {
    const op = this.find(idUser);
    const merchants = op ? [...op.merchants] : [];
    return { companyWide: merchants.length === 0, merchants };
  }
}
